import type { ReactElement } from "react"
import { Fragment, useState } from "react"

import { useTranslation } from "react-i18next"
import { useMutation } from "react-query"
import { useDispatch } from "react-redux"
import { Link, useNavigate } from "react-router-dom"

import * as Yup from "yup"
import { ArrowBackIosNewOutlined } from "@mui/icons-material"
import { Grid, useMediaQuery } from "@mui/material"
import type { CredentialResponse } from "@react-oauth/google"
import { GoogleLogin } from "@react-oauth/google"
import {
  Button,
  InputPassword,
  InputText,
  NotificationUtils,
  Snackbar,
  Typography,
} from "@synapse-analytics/synapse-ui"
import type { AxiosError, AxiosResponse } from "axios"
import { Form, FormikProvider, useFormik, useFormikContext } from "formik"
import { t } from "i18next"
import { type JwtPayload, jwtDecode } from "jwt-decode"

import { Divider } from "../../../components/core/Divider"
import { Tick } from "../../../components/status/Tick"
import { SidAPI } from "../../../services/SidAPI"
import type { CustomRegistrationInput } from "../../../types/custom/authentication"
import type { CustomRegistration } from "../../../types/generated/authentication/CustomRegistration"
import type { TokenObtainPairResponse } from "../../../types/generated/authentication/TokenObtainPairResponse"
import { doesNotContainSubstrings, getEnvVar, isEnvVariableTruthy } from "../../../utils/genericHelpers"
import { clearAuthData, setAuthTokens } from "../Slice"
import type { SignUpFormikValues, SignUpProps } from "../interfaces"
import { EntryDisclaimer } from "./EntryDisclaimer"
import { VerificationContainer as Verification } from "./VerificationScreen"

import styles from "./SignUp.module.scss"

/**
 * SignUp component handles the multi-step signup form UI and authentication states
 *
 * @param {AxiosError} signUpError - Error details if signup failed
 * @param {boolean} isGoogleAccVerified - Indicates if Google account is verified
 * @param {boolean} isGoogleLoginFailure - Indicates if Google signup/login failed
 * @param {number} activeStep - Current step in the signup flow (0: initial form, 1: password setup)
 * @param {(step: number) => void} setActiveStep - Function to update current step
 * @param {(response: CredentialResponse) => Promise<void>} onGoogleLoginSuccess - Callback for successful Google authentication
 * @returns {ReactElement} Multi-step signup form with authentication states
 */
export function SignUp(props: Readonly<SignUpProps>): ReactElement {
  const { signUpError, isGoogleAccVerified, isGoogleLoginFailure, activeStep, setActiveStep, onGoogleLoginSuccess } =
    props

  const { t, i18n } = useTranslation("auth")

  const { values, errors, touched, isSubmitting, isValid, dirty, status, handleChange, handleBlur, submitForm } =
    useFormikContext<SignUpFormikValues>()

  const breakpointMatch = useMediaQuery("(max-width:400px)")

  const isGoogleSigninEnabled = isEnvVariableTruthy(String(getEnvVar("KONAN_SSO_GOOGLE_ENABLED")))

  const steps = [
    {
      component: (
        <Fragment>
          <Grid container item xs={12} justifyContent="start" gap={1}>
            <Typography variant="h1-bold">{t("signup.title")}</Typography>

            <Typography variant="a">
              {t("signup.subtitle")}{" "}
              {/* TODO:: replace with SUI anchor component in the revamp leaving the styles inline to remember to remove them when revamping */}
              <Link to="/login" className={styles.link}>
                {t("signup.loginLink")}
              </Link>
            </Typography>
          </Grid>

          <Grid container justifyContent="space-between">
            <Grid item xs={6} pr={1}>
              <InputText
                fullWidth
                required
                hideDescription
                id="firstName"
                label={t("signup.firstName.label")}
                placeholder={t("signup.firstName.placeholder")}
                value={values.firstName}
                error={Boolean(errors.firstName) && touched.firstName && errors.firstName}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disabled={isSubmitting}
              />
            </Grid>
            <Grid item xs={6}>
              <InputText
                fullWidth
                hideDescription
                id="lastName"
                label={t("signup.lastName.label")}
                placeholder={t("signup.lastName.placeholder")}
                value={values.lastName}
                error={Boolean(errors.lastName) && touched.lastName && errors.lastName}
                handleChange={handleChange}
                handleBlur={handleBlur}
                disabled={isSubmitting}
              />
            </Grid>
          </Grid>

          <InputText
            fullWidth
            required
            id="email"
            label={t("signup.email.label")}
            placeholder={t("signup.email.placeholder")}
            value={values.email}
            error={touched.email && errors.email}
            handleChange={handleChange}
            handleBlur={handleBlur}
            disabled={isSubmitting}
          />

          <Button
            fullWidth
            id="continue-button"
            variant="primary"
            type="submit"
            onClick={() => setActiveStep(activeStep + 1)}
            disabled={
              !firstStepValidationSchema.isValidSync({
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
              })
            }
          >
            {t("signup.continue")}
          </Button>

          {/* TODO:: configure google signup to view a different signup page,
          if there are missing data in the google account used to register */}
          {isGoogleSigninEnabled && (
            <Grid container item justifyContent="center" gap={3}>
              <Divider text={t("signup.or")} mt={1.25} mb={1.25} />

              <GoogleLogin
                width={breakpointMatch ? 252 : 312}
                theme="outline"
                size="medium"
                text="signup_with"
                context="signup"
                onSuccess={onGoogleLoginSuccess}
                useOneTap={false}
                auto_select={false}
                locale={i18n.language}
              />
            </Grid>
          )}
        </Fragment>
      ),
    },
    {
      component: (
        <Fragment>
          <Grid container xs={12} justifyContent="start" gap={1.5}>
            <Button
              id="back-button"
              data-testid="back-button"
              variant="secondary"
              onClick={() => setActiveStep(activeStep - 1)}
            >
              <ArrowBackIosNewOutlined className={styles.backIcon} />
            </Button>
            <Grid container xs={12} gap={1}>
              <Typography variant="h1-bold">{t("signup.setPassword")}</Typography>
              <Typography variant="a">
                {t("signup.subtitle")}{" "}
                {/* TODO:: replace with SUI anchor component in the revamp leaving the styles inline to remember to remove them when revamping */}
                <Link to="/login" className={styles.link}>
                  {t("signup.loginLink")}
                </Link>
              </Typography>
            </Grid>
          </Grid>

          <InputPassword
            fullWidth
            hideDescription
            id="password"
            value={values.password}
            error={
              Boolean(errors.password) && errors.password && typeof errors.password !== "string"
                ? errors.password[0]
                : errors.password
            }
            handleChange={handleChange}
            handleBlur={handleBlur}
            disabled={isSubmitting}
          />

          <InputPassword
            fullWidth
            confirmPassword
            hideDescription
            id="rePassword"
            value={values.rePassword}
            handleChange={handleChange}
            handleBlur={handleBlur}
            disabled={isSubmitting}
          />

          <Grid container xs={12} justifyContent="start" gap={1.5}>
            <Tick
              id="min-8-chars"
              variant={values.password.length >= 8 ? "positive" : "base"}
              text={t("signup.password.minLength")}
            />
            <Tick
              id="min-letters"
              variant={
                !isNaN(Number(values.password)) || [null, undefined, ""].includes(values.password) ? "base" : "positive"
              }
              text={t("signup.passwordRequirements.includeLetters")}
            />
            <Tick
              id="no-username-email"
              variant={
                doesNotContainSubstrings(values.password, [values.firstName, values.lastName, values.email])
                  ? "positive"
                  : "base"
              }
              text={t("signup.passwordRequirements.noUsernameEmail")}
            />
            <Tick
              id="password-match"
              variant={values.password.length !== 0 && values.password === values.rePassword ? "positive" : "base"}
              text={t("signup.confirmPassword.mismatch")}
            />
          </Grid>

          <Button
            fullWidth
            id="create-account-button"
            variant="primary"
            type="submit"
            onClick={() => submitForm()}
            disabled={isSubmitting || !(dirty && isValid)}
            isLoading={isSubmitting}
          >
            {t("signup.submit")}
          </Button>
        </Fragment>
      ),
    },
  ]

  return (
    <Grid item container className={styles.mainContainer}>
      {/* Network error; shown if client cant reach api for any reason */}
      {status && status?.error && signUpError?.message === "Network Error" && (
        <Snackbar fullWidth severity="error" description={t("login.networkError")} />
      )}

      {/**
       * Google SignUp errors
       * 1. google account isn't verified
       * 2. Google account signup failure
       */}
      {!isGoogleAccVerified && (
        <Snackbar fullWidth severity="error" description={t("signup.googleAccountNotVerified")} />
      )}

      {isGoogleLoginFailure && <Snackbar fullWidth severity="error" description={t("signup.googleSignUpFailure")} />}

      <Form>
        <Grid container className={styles.formContainer}>
          {steps[activeStep]?.component}
        </Grid>
      </Form>

      <EntryDisclaimer type="signup" />
    </Grid>
  )
}

// validation schema of the first sign up step
const firstStepValidationSchema = Yup.object({
  firstName: Yup.string()
    .trim()
    .required(t("signup.firstName.required", { ns: "auth" })),
  lastName: Yup.string().trim(),
  email: Yup.string()
    .email(t("signup.email.invalid", { ns: "auth" }))
    .trim()
    .required(t("signup.email.required", { ns: "auth" })),
})

// validation schema of the second sign up step
const secondStepValidationSchema = Yup.object({
  password: Yup.string()
    .required(t("signup.password.required", { ns: "auth" }))
    .min(8, t("signup.password.minLength", { ns: "auth" }))
    .test("isNumber", t("signup.passwordRequirements.includeLetters", { ns: "auth" }), (value) => isNaN(Number(value)))
    .test(
      "doesNotContainSubstrings",
      t("signup.passwordRequirements.noUsernameEmail", { ns: "auth" }),
      (value, context) => {
        return doesNotContainSubstrings(value, [
          context.parent.firstName,
          context.parent.lastName,
          context.parent.email,
        ])
      },
    ),
  rePassword: Yup.string()
    .required(t("signup.confirmPassword.required", { ns: "auth" }))
    .oneOf([Yup.ref("password")], t("signup.confirmPassword.mismatch", { ns: "auth" })),
})

// The validation schemas of both steps is joined together to create the formik validation schema
// eslint-disable-next-line react-refresh/only-export-components
export const validationSchema = firstStepValidationSchema.concat(secondStepValidationSchema)

/**
 * Container component that manages the signup state and form submission logic.
 * Handles both traditional and Google OAuth signup/authentication flows.
 *
 * @returns {ReactElement} The signup form container with state management
 */
export function SignUpContainer(): ReactElement {
  const navigate = useNavigate()

  const dispatch = useDispatch()

  // Track Google authentication states
  const [isGoogleAccVerified, setIsGoogleAccVerified] = useState<boolean>(true)
  const [isGoogleLoginFailure, setIsGoogleLoginFailure] = useState<boolean>(false)
  // Controls which step of the signup form is displayed (0: personal info, 1: password setup)
  const [activeStep, setActiveStep] = useState<number>(0)

  // Mutation for handling traditional email/password signup
  const signupMutation = useMutation<AxiosResponse<CustomRegistration>, AxiosError, CustomRegistrationInput>(
    SidAPI.signup,
    {
      onError: (e) => {
        // Map API validation errors to form fields
        if (e.response?.data) {
          formik.setErrors(e.response.data)
        }

        // Handle non-forbidden errors with toast notifications
        if (e?.response?.status !== 403) {
          const errorData = e.response?.data

          // Error data is returned as an object with keys corresponding to the fields that have errors,
          // and the value being an array of error messages for that field
          if (errorData && typeof errorData === "object") {
            // This loop is done to go over all of the arrays returned and show all the error messages
            for (const value of Object.values(errorData)) {
              value.forEach((errorMessage: string) => {
                NotificationUtils.toast(errorMessage, { severity: "error" })
              })
            }
          }
        }
      },
    },
  )

  // Mutation for handling Google OAuth login
  const googleLoginMutation = useMutation<AxiosResponse<TokenObtainPairResponse>, AxiosError, string>(
    SidAPI.googleLogin,
  )

  // Initialize form with Formik, including validation and submission handling
  const formik = useFormik({
    // TODO: Formik names should be the same as the API response so that Errors can be correctly mapped
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      password: "",
      rePassword: "",
    },
    validationSchema: validationSchema,
    onSubmit: async (values, { setStatus }) => {
      try {
        // Logout the user before registering, this is to flush the access token from the localStorage
        dispatch(clearAuthData())
        await signupMutation.mutateAsync(values)
      } catch (e) {
        setStatus({
          error: t("signup.error", { ns: "auth" }),
        })
      }
    },
  })

  const onGoogleLoginSuccess = async (credentialResponse: CredentialResponse): Promise<void> => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let response: any

    // Checks if google account is verified before Login/SignUp
    // if account isn't verified login isn't attempted
    const decodedToken = jwtDecode<JwtPayload & { email_verified: boolean }>(credentialResponse.credential as string)
    if (decodedToken.email_verified === false) {
      setIsGoogleAccVerified(false)
      return
    }

    try {
      // First attempt to signup with Google
      response = await SidAPI.googleSignup(credentialResponse.credential as string)
    } catch (error) {
      try {
        // If signup fails, attempt to login instead
        response = await googleLoginMutation.mutateAsync(credentialResponse.credential as string)
      } catch (error) {
        setIsGoogleLoginFailure(true)
        return
      }
    }

    // Logs the user in after login/signup
    const data = response.data
    dispatch(setAuthTokens({ access: data.access, refresh: data.refresh }))
    navigate("/", { replace: true })
  }

  // Show verification screen after successful signup or if email verification is required
  if (signupMutation.isSuccess || googleLoginMutation.error?.response?.status === 403) {
    return (
      <Verification
        isPostSignup
        email={formik.values.email}
        createAccount={() => {
          setActiveStep(0)
          signupMutation.reset()
        }}
      />
    )
  }

  return (
    <FormikProvider value={formik}>
      <SignUp
        signUpError={signupMutation.error}
        isGoogleAccVerified={isGoogleAccVerified}
        isGoogleLoginFailure={isGoogleLoginFailure}
        activeStep={activeStep}
        setActiveStep={setActiveStep}
        onGoogleLoginSuccess={onGoogleLoginSuccess}
      />
    </FormikProvider>
  )
}
